// Pages and their tabs the global search bar can open (Settings pages are in settingsSearch.js).
//
// Matching is the same static keyword map as Settings: every entry has a title, the page
// it belongs to (`section`), the URL it opens and the words people are likely to type.
// Tabs are opened through their own route or a URL parameter (`?tab=`, `?key=`) that the
// page reads. To add a page or tab, add an entry here.
//
// An entry is only returned when the user can open it: the same module checks as the
// route (src/Router/routes.js) and as the page itself.

import { isActive, orgModules, rankEntries, userModules } from "./settingsSearch";

const userHas = (name) => isActive(userModules(), name);
const orgHas = (name) => isActive(orgModules(), name);
// for pages that only block a module the user has switched off (not a missing one)
const userNotOff = (name) => userModules().find?.((m) => m?.moduleName === name)?.active !== false;

// Route rule: when the user's modules list the module, it must be active for the user and the organisation.
const routeAllows = (name) => {
  const userModule = userModules().find?.((m) => m?.moduleName === name);
  return !userModule || (!!userModule.active && orgHas(name));
};

const organization = () => {
  try {
    return JSON.parse(localStorage.getItem("organization") || "{}") || {};
  } catch (e) {
    return {};
  }
};

// Who can open each page (route check + the page's own check)
const PAGE_ACCESS = {
  members: () => routeAllows("members") && userHas("members"),
  yahrzeit: () => routeAllows("yahrzeit") && userHas("members"),
  education: () => routeAllows("school") && userHas("education") && orgHas("education"),
  // same as the sidebar item: the user's finance module and the organisation's fundraising module
  fundraising: () => routeAllows("finance") && userHas("finance") && orgHas("fundraising"),
  donations: () => routeAllows("finance") && userNotOff("donation"),
  finance: () => routeAllows("finance") && userHas("finance"),
  easycare: () => routeAllows("pastoral") && userHas("pastoral"),
  events: () => routeAllows("events") && userHas("events") && orgHas("events"),
  forms: () => routeAllows("forms") && userHas("forms") && orgHas("forms"),
  communication: () => routeAllows("communication") && userHas("communication") && orgHas("communication"),
  segments: () => routeAllows("report") && userHas("report"),
};

const MEMBERS = "Members";
const EDUCATION = "Education";
const FINANCES = "Finances";
const EASYCARE = "Easycare";
const EVENTS = "Events";
const FORMS = "Forms";
const COMMUNICATIONS = "Communications";
const SEGMENTS = "Segments";

export const PAGE_SEARCH_ENTRIES = [
  // Members
  { id: "members", page: "members", title: "Members", section: MEMBERS, url: "/members", keywords: ["member list", "families", "households", "directory"] },
  { id: "members-accounts", page: "members", title: "Accounts", section: MEMBERS, url: "/members?tab=account", keywords: ["member accounts", "families", "households", "organizations"] },
  { id: "members-people", page: "members", title: "People", section: MEMBERS, url: "/members?tab=people", keywords: ["contacts", "individuals", "persons", "family members"] },

  // Yahrzeits
  { id: "yahrzeits", page: "yahrzeit", title: "Yahrzeits", section: "Yahrzeits", url: "/yahrzeit", keywords: ["yahrzeit", "yartzeit", "deceased", "memorial", "mourners", "kaddish", "hebrew date"] },

  // Education
  { id: "education", page: "education", title: "Education", section: EDUCATION, url: "/religious-schools", keywords: ["school", "religious school", "hebrew school", "classes", "students", "enrollment"] },
  { id: "education-active", page: "education", title: "Active Classes", section: EDUCATION, url: "/religious-schools?tab=active", keywords: ["classes", "current classes", "school"] },
  { id: "education-archived", page: "education", title: "Archived Classes", section: EDUCATION, url: "/religious-schools?tab=archived", keywords: ["old classes", "past classes", "school"] },
  { id: "education-bulk", page: "education", title: "Bulk Registration", section: EDUCATION, url: "/religious-schools?tab=bulk_registration", keywords: ["bulk classes", "bulk enrollment", "class registration"] },

  // Fundraising
  { id: "fundraising", page: "fundraising", title: "Fundraising Campaigns", section: "Fundraising", url: "/fundraising", keywords: ["fundraising", "campaigns", "donors", "appeal", "goal"] },

  // Donations
  { id: "donations", page: "donations", title: "Tribute Letters", section: "Donations", url: "/donation", keywords: ["donations", "tribute", "thank you letters", "acknowledgement letters"] },

  // Finances
  { id: "finances", page: "finance", title: "Finances", section: FINANCES, url: "/finance", keywords: ["finance", "billing", "money"] },
  { id: "finance-transactions", page: "finance", title: "Transactions", section: FINANCES, url: "/finance", keywords: ["charges", "payments", "donations", "credit on account", "coa"] },
  { id: "finance-bulk-billing", page: "finance", title: "Bulk Billing", section: FINANCES, url: "/finance/batch", keywords: ["batch billing", "bulk charges", "batch charges", "dues"] },
  { id: "finance-documents", page: "finance", title: "Documents", section: FINANCES, url: "/finance/documents", keywords: ["invoices", "receipts", "statements", "tax reports", "single documents"] },
  { id: "finance-bulk-documents", page: "finance", title: "Bulk Documents", section: FINANCES, url: "/finance/batch-documents", keywords: ["batch documents", "bulk statements", "bulk receipts", "bulk tax reports"] },
  { id: "finance-deposits", page: "finance", title: "Deposits & Sync", section: FINANCES, url: "/finance/deposits", keywords: ["deposits", "awaiting to batch", "quickbooks sync", "bank deposit"] },
  { id: "finance-deposit-batches", page: "finance", title: "Deposit Batches", section: FINANCES, url: "/finance/deposits/pending", keywords: ["batches", "pending batches", "deposited"] },
  { id: "finance-payment-plans", page: "finance", title: "Payment Plans", section: FINANCES, url: "/finance/payment-plans", keywords: ["installments", "recurring", "pledges", "subscriptions"] },

  // Easycare
  { id: "easycare", page: "easycare", title: "Easycare", section: EASYCARE, url: "/easycare", keywords: ["pastoral", "pastoral care", "care"] },
  { id: "easycare-dashboard", page: "easycare", title: "Easycare Dashboard", section: EASYCARE, url: "/easycare?tab=dashboard", keywords: ["tasks", "feed", "pastoral events"] },
  { id: "easycare-task-automation", page: "easycare", title: "Task Automation", section: EASYCARE, url: "/easycare?tab=task-automation", keywords: ["triggers", "automated tasks", "automation"] },
  { id: "easycare-update-reports", page: "easycare", title: "Update Reports", section: EASYCARE, url: "/easycare?tab=schedule-reports", keywords: ["schedule reports", "scheduled reports"] },

  // Events
  { id: "events", page: "events", title: "Events", section: EVENTS, url: "/newEvents", keywords: ["calendar", "tickets", "event registration"] },
  { id: "events-active", page: "events", title: "Active Events", section: EVENTS, url: "/newEvents?tab=active", keywords: ["upcoming events", "current events"] },
  { id: "events-archived", page: "events", title: "Archived Events", section: EVENTS, url: "/newEvents?tab=archived", keywords: ["past events", "old events"] },

  // Forms
  { id: "forms", page: "forms", title: "Forms", section: FORMS, url: "/forms", keywords: ["form builder", "registration forms", "surveys"] },
  { id: "forms-active", page: "forms", title: "Active Forms", section: FORMS, url: "/forms?key=active", keywords: ["current forms"] },
  { id: "forms-archived", page: "forms", title: "Archived Forms", section: FORMS, url: "/forms?key=archived", keywords: ["old forms", "past forms"] },
  {
    id: "forms-membership",
    page: "forms",
    title: "Membership Forms",
    section: FORMS,
    url: "/forms?key=membership",
    keywords: ["membership", "renewal", "join"],
    // the tab is only shown when the organisation has membership forms
    when: () => !!organization()?.allowMembership,
  },

  // Communications
  { id: "communications", page: "communication", title: "Communications", section: COMMUNICATIONS, url: "/communication", keywords: ["email", "sms", "messages"] },
  { id: "communication-audiences", page: "communication", title: "Audiences", section: COMMUNICATIONS, url: "/communication?tab=Audiences", keywords: ["recipients", "mailing lists", "audience"] },
  { id: "communication-campaigns", page: "communication", title: "Campaigns", section: COMMUNICATIONS, url: "/communication?tab=Campaigns", keywords: ["email campaigns", "newsletters", "email blast"] },
  { id: "communication-templates", page: "communication", title: "Templates", section: COMMUNICATIONS, url: "/communication?tab=Templates", keywords: ["email templates", "message templates"] },
  { id: "communication-automation", page: "communication", title: "Automation", section: COMMUNICATIONS, url: "/communication?tab=Automation", keywords: ["automations", "automated emails", "birthday emails", "yahrzeit reminders"] },

  // Segments
  { id: "segments", page: "segments", title: "Segments", section: SEGMENTS, url: "/report", keywords: ["reports", "report"] },
  { id: "segments-all-reports", page: "segments", title: "All Reports", section: SEGMENTS, url: "/report?tab=all-reports", keywords: ["reports"] },
  { id: "segments-favorites", page: "segments", title: "Favorite Reports", section: SEGMENTS, url: "/report?tab=favorites", keywords: ["favorites", "saved reports"] },
  { id: "segments-financial-reports", page: "segments", title: "Financial Reports", section: SEGMENTS, url: "/report?tab=financial-reports", keywords: ["finance reports", "billing reports", "payment reports"] },
];

const entryAllowed = (entry) => !!PAGE_ACCESS[entry.page]?.() && (!entry.when || entry.when());

// a short query like "fi" can match many tabs; keep the dropdown readable
const MAX_PAGE_RESULTS = 8;

// Pages / tabs matching the typed text that this user can open, best first.
export const searchPages = (query) => rankEntries(PAGE_SEARCH_ENTRIES.filter(entryAllowed), query).slice(0, MAX_PAGE_RESULTS);

// Whether a saved page result (e.g. in recent searches) may still be shown to this user
export const isPageResultVisible = (item) =>
  PAGE_SEARCH_ENTRIES.some((entry) => entry.url === item?.url && entry.title === item?.name && entryAllowed(entry));
